import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import {
    captureRefreshSlot,
    clearActiveTokens,
    getAccessToken,
    getRefreshToken,
    isAccessTokenPresent,
    isRefreshTokenPresent,
    serverLogOut,
    storeRefreshedTokens,
} from "../../utils/AuthUtils/AuthUtils";
import store from "../../store";
import { PublicRoutes } from "../../utils/appEnums/AppEnums";
import { BO_CORE_DEFAULT_NUMBER_VALUE, isPathPublic, setAxiosInstances } from "../../utils/AppUtils";
import { API_BASE_URL, SANDBOX_API_BASE_URL, OAUTH_BASE_URL } from "../app.config";
import { DefinedAxiosInstances } from "../../utils/constant-data/Axios";
import { refreshAccessToken } from "./apis/auth/auth";
import { setSimpleSpinner } from "../../reducers/spinnerReducer";

interface Instance {
    name: string;
    config: AxiosRequestConfig;
}

let isAlreadyFetchingAccessToken = false;

// * debug
// export const AXIO_API_BASE_URL: string = `${API_BASE_URL?.replace("://", "://gosho.")}`;
export const AXIO_API_BASE_URL = `${API_BASE_URL}`;

export const cancelToken = axios.CancelToken.source();

export const cancelTokenModals = axios.CancelToken.source();

export const cancelTokenTimeOut: any = 0;

export const cancelTokenTimeOutModals: any = 0;

/**
 * Instances created WITHOUT the shared auth interceptors (see `createInstances`): no bearer is
 * attached from the global token store and — critically — the 401 refresh/logout response
 * interceptor is not applied, so a failure on one of these can never refresh tokens or log the
 * user out.
 *
 * PUBLIC is here because a stray 401 on a cosmetic unauthenticated read must not touch the session
 * (NG-8457). The SANDBOX_* pair is here because it talks to a *different environment* (NG-8648): the
 * token store is keyed by tenant id alone, so a downstream tenant would collide with an upstream
 * tenant of the same id. Callers on those instances pass their bearer explicitly.
 */
const BARE_INSTANCE_NAMES: ReadonlySet<string> = new Set([
    DefinedAxiosInstances.PUBLIC.name,
    DefinedAxiosInstances.SANDBOX_V2.name,
    DefinedAxiosInstances.SANDBOX_V3.name,
]);

/**
 * NG-8648: the downstream environment provisioned alongside this one during developer registration.
 * Absent when no downstream is configured (the local/test default), in which case the instances are not
 * created and `getAxiosInstances().SANDBOX_V2` is undefined — callers gate on
 * `FEATURE_DUAL_ENV_DEVELOPER_SIGNUP` rather than probing for them.
 */
const sandboxInstanceConfigs = (): Instance[] => {
    if (!SANDBOX_API_BASE_URL) {
        return [];
    }
    return [
        {
            name: DefinedAxiosInstances.SANDBOX_V2.name,
            config: {
                baseURL: `${SANDBOX_API_BASE_URL}${DefinedAxiosInstances.SANDBOX_V2.urlPostFix}`,
                cancelToken: cancelToken.token,
            },
        },
        {
            name: DefinedAxiosInstances.SANDBOX_V3.name,
            config: {
                baseURL: `${SANDBOX_API_BASE_URL}${DefinedAxiosInstances.SANDBOX_V3.urlPostFix}`,
                cancelToken: cancelToken.token,
                headers: {
                    "x-path": "v3",
                },
            },
        },
    ];
};

const instanceConfigs: Instance[] = [
    {
        name: DefinedAxiosInstances.V1.name,
        config: {
            baseURL: `${AXIO_API_BASE_URL}${DefinedAxiosInstances.V1.urlPostFix}`,
            cancelToken: cancelToken.token,
        },
    },
    {
        name: DefinedAxiosInstances.V2.name,
        config: {
            baseURL: `${AXIO_API_BASE_URL}${DefinedAxiosInstances.V2.urlPostFix}`,
            cancelToken: cancelToken.token,
        },
    },
    {
        name: DefinedAxiosInstances.V3.name,
        config: {
            baseURL: `${AXIO_API_BASE_URL}${DefinedAxiosInstances.V3.urlPostFix}`,
            cancelToken: cancelToken.token,
            headers: {
                "x-path": "v3",
            },
        },
    },
    {
        name: DefinedAxiosInstances.NOV.name,
        config: {
            baseURL: `${AXIO_API_BASE_URL}${DefinedAxiosInstances.NOV.urlPostFix}`,
            cancelToken: cancelToken.token,
        },
    },
    {
        // Public, unauthenticated reads (branding config, NG-8457). Created WITHOUT the shared auth
        // interceptors (see createInstances): no bearer is attached and — critically — the 401
        // refresh/logout response interceptor is NOT applied, so a stray 401 on a cosmetic public call can
        // never refresh tokens or log the user out. The short timeout lets a stalled endpoint fall through
        // to defaults instead of holding the login page on a spinner.
        name: DefinedAxiosInstances.PUBLIC.name,
        config: {
            baseURL: `${AXIO_API_BASE_URL}${DefinedAxiosInstances.PUBLIC.urlPostFix}`,
            timeout: 5000,
            cancelToken: cancelToken.token,
        },
    },
    {
        name: DefinedAxiosInstances.AUTH.name,
        config: {
            baseURL: `${OAUTH_BASE_URL}${DefinedAxiosInstances.AUTH.urlPostFix}`,
            cancelToken: cancelToken.token,
        },
    },
    // NG-8648: cross-environment instances for developer registration. Only created when a
    // downstream environment is configured — see sandboxInstanceConfigs above.
    ...sandboxInstanceConfigs(),
    {
        name: DefinedAxiosInstances.graphqlInstance.name,
        config: {
            baseURL: AXIO_API_BASE_URL,
            withCredentials: true,
            headers: {
                "X-Requested-With": "XMLHttpRequest",
                "Time-Zone": Cookies.get("Time-Zone"),
            },
            cancelToken: cancelToken.token,
        },
    },
];

// static instances: AxiosInstances;

// refresh token related

let subscribers = [];

export function getAlreadyFetchingAccessToken() {
    return isAlreadyFetchingAccessToken;
}

export function setAlreadyFetchingAccessToken(value) {
    isAlreadyFetchingAccessToken = value;
}

export const addSubscriber = (callback: any) => {
    // @ts-ignore
    subscribers.push(callback);
};

export const onAccessTokenFetched = (access_token: string) => {
    // @ts-ignore
    subscribers = subscribers.filter((callback) => callback(access_token));
};

export const appendAuthorizationBearerToken = (config: any) => {
    const shallowConfig = config;
    const withCredentials = config.withCredentials ?? true;
    if (!withCredentials) {
        return config;
    }
    shallowConfig.headers.Authorization = `Bearer ${getAccessToken()}`;
    return shallowConfig;
};

export const handleAxiosResponseError = (error: any) => {
    const shallowError = error;
    if (shallowError.message === "Network Error" || shallowError.message === "canceled") {
        return Promise.reject(shallowError);
    }

    const {
        config,
        response: { status },
    } = shallowError;

    const originalRequest = config;
    if (status === 401 && isRefreshTokenPresent()) {
        if (!getAlreadyFetchingAccessToken()) {
            setAlreadyFetchingAccessToken(true);
            // Pin the slot before the round-trip: a tenant switch or assume-mode flip while the
            // refresh is in flight must not re-route the response into another tenant's slot.
            const refreshSlot = captureRefreshSlot();
            refreshAccessToken(getRefreshToken() ?? "")
                .then((response) => {
                    const accepted = storeRefreshedTokens(
                        response.data.access_token,
                        response.data.refresh_token,
                        response.data.expires_in,
                        response.data.refresh_expires_in,
                        refreshSlot,
                    );
                    if (!accepted) {
                        // The BE minted the token for another tenant — same exit as a failed refresh.
                        return Promise.reject(new Error("Refreshed token was minted for another tenant"));
                    }
                    setAlreadyFetchingAccessToken(false);
                    onAccessTokenFetched(response.data.access_token);
                    return undefined;
                })
                .catch(() => {
                    clearActiveTokens();
                    serverLogOut().then(() => store.dispatch(setSimpleSpinner(false)));
                })
                .finally(() => {
                    setAlreadyFetchingAccessToken(false);
                });
        }

        return new Promise((resolve) => {
            addSubscriber((access_token: any) => {
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                resolve(axios(originalRequest));
            });
        });
    }

    if (status === 401 && !isRefreshTokenPresent() && !isAccessTokenPresent()) {
        if (!isPathPublic(window.location.pathname)) {
            window.location.href = PublicRoutes.LOGIN;
        } else {
            return Promise.reject(shallowError);
        }
    } else if (
        shallowError.request.responseType === "blob" &&
        shallowError.response.data instanceof Blob &&
        shallowError.response.data.type &&
        shallowError.response.data.type.toLowerCase().indexOf("json") !== BO_CORE_DEFAULT_NUMBER_VALUE
    ) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === "string") {
                    shallowError.response.data = JSON.parse(reader.result);
                }
                resolve(Promise.reject(shallowError));
            };

            reader.onerror = () => {
                reject(shallowError);
            };

            reader.readAsText(shallowError.response.data);
        });
    } else {
        return Promise.reject(shallowError);
    }
    return undefined;
};

const interceptorsConfig = (instance: AxiosInstance): void => {
    instance.interceptors.request.use(
        (config) => {
            return appendAuthorizationBearerToken(config);
        },
        (error) => Promise.reject(error),
    );

    instance.interceptors.response.use((response) => response, handleAxiosResponseError);
};

export const createInstances = (instances: Instance[]) => {
    const axiosInstances: any = {};
    instances.forEach((instance) => {
        axiosInstances[instance.name] = axios.create(instance.config);
        // Some instances are intentionally bare — no auth request/response interceptors (no bearer,
        // no 401 refresh/logout cascade) so they can never affect the session. See BARE_INSTANCE_NAMES.
        if (!BARE_INSTANCE_NAMES.has(instance.name)) {
            interceptorsConfig(axiosInstances[instance.name]);
        }
    });
    setAxiosInstances({ ...axiosInstances });
};

export const initialize = () => {
    createInstances(instanceConfigs);
};

export const all = <T>(values: (T | Promise<T>)[]): Promise<T[]> => {
    return axios.all(values);
};

export const isHttpErrorMessageAvailable = (error: any): boolean => {
    return axios.isAxiosError(error) && !!error.response?.data?.message;
};
